<template>
  <div class="dashboard">
    <DashboardCard :title="'Overview'">
      <DashboardEmpty v-if="!overview.initialized" :section="'Overview'" :disabled="computeLoadingState" @initialize="getStatsOverview" />
      <ElRow v-else type="flex" :gutter="20" style="flex-wrap: wrap">
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Active Taxpayers" :value="formatCount(taxPayersStats.total_active_payers)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Inactive Taxpayers" :value="formatCount(taxPayersStats.total_inactive_payers)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Deregistered Taxpayers" :value="formatCount(taxPayersStats.total_deregistered_payers)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Dormant Taxpayers" :value="formatCount(taxPayersStats.total_dormant_payers)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Individual Taxpayers" :value="formatCount(taxPayersStats.total_individual)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Corporate Taxpayers" :value="formatCount(taxPayersStats.total_corporate)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.taxPayers" label="Tax Stations" :value="formatCount(taxPayersStats.stations)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.assessments" label="Debt" :value="formatAmount(assessmentsStats.debt)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.payments" label="Daily Volume" :value="formatAmount(paymentsStats.daily_volume)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.payments" label="Monthly Volume" :value="formatAmount(paymentsStats.month_volume)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.payments" label="Annual Volume" :value="formatAmount(paymentsStats.annual_volume)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="overview.directAssessments" label="Direct Debt" :value="formatAmount(directAssessmentsStats.direct_debt)" />
        </ElCol>
      </ElRow>
    </DashboardCard>

    <DashboardCard :title="'Filters'">
      <ElForm :model="filters" label-position="top">
        <ElRow type="flex" :gutter="15" align="bottom">
          <ElCol :md="5">
            <ElFormItem label="Tax Region">
              <ElSelect v-model="filters.tax_region_id" @change="getTaxStations">
                <ElOption
                  v-for="region in taxRegions"
                  :key="region.id" :value="region.id" :label="region.name">{{ region.name }}</ElOption>
              </ElSelect>
            </ElFormItem>
          </ElCol>
          <ElCol :md="7">
            <ElFormItem label="Tax Station">
              <ElSelect v-model="filters.tax_office_id" filterable placeholder="Search for Tax Station" clearable>
                <ElOption
                  v-for="station in taxStations.list" :disabled="taxStations.loading"
                  :key="station.id" :value="station.id" :label="station.name">{{ station.name }}</ElOption>
              </ElSelect>
            </ElFormItem>
          </ElCol>
          <ElCol :md="4">
            <ElFormItem label="Start Date">
              <ElDatePicker
                v-model="filters.start_date"
                type="date"
                placeholder="Select start date"
                format="dd MMM, yyyy"
                value-format="yyyy-MM-dd"
                :picker-options="computeDatepickerOptions"
              >
              </ElDatePicker>
            </ElFormItem>
          </ElCol>
          <ElCol :md="4">
            <ElFormItem label="End Date">
              <ElDatePicker
                v-model="filters.end_date"
                type="date"
                placeholder="Select end date"
                format="dd MMM, yyyy"
                value-format="yyyy-MM-dd"
              >
              </ElDatePicker>
            </ElFormItem>
          </ElCol>
          <ElCol :md="4">
            <ElFormItem label="Actions">
              <ElButton type="danger" size="mini" :disabled="computeLoadingState" @click="resetStats">Reset</ElButton>
              <ElButton type="primary" size="mini" :disabled="computeLoadingState" @click="filterStats">Filter</ElButton>
            </ElFormItem>
          </ElCol>
        </ElRow>
      </ElForm>
    </DashboardCard>

    <DashboardCard
      :loading="computeLoadingState"
      :title="'Bills'"
      :subtitle="dateLabel + ' (All Offices)'"
      :show-reload="bills.initialized"
      @reload="getBillsStats">
      <DashboardEmpty v-if="!bills.initialized" :section="'Bills'" :disabled="computeLoadingState" @initialize="getBillsStats" />
      <ElRow v-else type="flex" :gutter="20" style="flex-wrap: wrap">
        <ElCol :md="8">
          <DashboardStat :loading="bills.loading" icon="count" label="Total Bills" :value="formatCount(bills.count)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="bills.loading" label="Amount Due" :value="formatAmount(bills.amount_due)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="bills.loading" label="Amount Paid" :value="formatAmount(bills.amount_paid)" />
        </ElCol>
        <ElCol :md="8">
          <DashboardStat :loading="bills.loading" label="Amount Outstanding" :value="formatAmount(bills.amount_outstanding)" />
        </ElCol>
      </ElRow>
    </DashboardCard>

    <DashboardCard
      :loading="computeLoadingState"
      :title="'Reports'"
      :subtitle="dateLabel"
      :show-reload="taxpayers.initialized"
      :content-min-height="!taxpayers.initialized ? 200 : 400"
      @reload="handleReportsReload">

      <DashboardEmpty v-if="!taxpayers.initialized" :section="'Reports'" :disabled="computeLoadingState" @initialize="getStatsAndTrend(true)" />
      <ElRow v-else type="flex">
        <ElCol :span="24">
          <ElTabs v-model="entity" @tab-click="getStatsAndTrend(false)">

            <!-- Taxpayers -->
            <ElTabPane :disabled="computeLoadingState" label="Taxpayers" name="taxpayers">
              <ElRow class="dashboard-graph" type="flex" :gutter="20">
                <DashboardLoader v-if="taxpayers.loading"  />
                <ElCol :md="8">
                  <DoughnutChart v-if="taxpayers.formattedData.stats" :data="taxpayers.formattedData.stats" />
                </ElCol>
                <ElCol :md="16">
                  <div class="dashboard-group">
                    <p>Total - ({{ formatCount(taxpayers.total) }})</p>
                    <ElRadioGroup v-model="taxpayers.group" size="mini" @change="getTrends('taxpayers')">
                      <ElRadioButton label="month">Month</ElRadioButton>
                      <ElRadioButton label="year">Year</ElRadioButton>
                    </ElRadioGroup>
                  </div>
                  <BarChart v-if="taxpayers.formattedData.trends" type="multiple" :data="taxpayers.formattedData.trends" />
                </ElCol>
              </ElRow>
            </ElTabPane>

            <!-- Returns -->
            <ElTabPane :disabled="computeLoadingState" label="Returns" name="returns">
              <ElRow class="dashboard-graph" type="flex" :gutter="20">
                <DashboardLoader v-if="returns.loading"  />
                <ElCol :md="8">
                  <DoughnutChart v-if="returns.formattedData.stats" :data="returns.formattedData.stats" />
                </ElCol>
                <ElCol :md="16">
                  <div class="dashboard-group">
                    <p>Total - ({{ formatCount(returns.total) }})</p>
                    <ElRadioGroup v-model="returns.group" size="mini" @change="getTrends('returns')" :disabled="returns.loading">
                      <ElRadioButton label="month">Month</ElRadioButton>
                      <ElRadioButton label="year">Year</ElRadioButton>
                    </ElRadioGroup>
                  </div>
                  <BarChart v-if="returns.formattedData.trends" type="multiple" :data="returns.formattedData.trends" />
                </ElCol>
              </ElRow>
            </ElTabPane>

            <!-- Assessments -->
            <ElTabPane :disabled="computeLoadingState" label="Assessments" name="assessments">
              <ElRow class="dashboard-graph" type="flex" :gutter="20">
                <DashboardLoader v-if="assessments.loading"  />
                <ElCol :md="8">
                  <DoughnutChart v-if="assessments.formattedData.stats" :data="assessments.formattedData.stats" />
                </ElCol>
                <ElCol :md="16">
                  <div class="dashboard-group">
                    <p>Total - ({{ formatCount(assessments.total) }})</p>
                    <ElRadioGroup v-model="assessments.group" size="mini" @change="getTrends('assessments')">
                      <ElRadioButton label="month">Month</ElRadioButton>
                      <ElRadioButton label="year">Year</ElRadioButton>
                    </ElRadioGroup>
                  </div>
                  <BarChart v-if="assessments.formattedData.trends" type="multiple" :data="assessments.formattedData.trends" />
                </ElCol>
              </ElRow>
            </ElTabPane>

            <!-- Objections -->
            <ElTabPane :disabled="computeLoadingState" label="Objections" name="objections">
                <DashboardObjection
                  ref="objectionsDashboard"
                  :active="entity === 'objections'"
                  :start-date="filters.start_date"
                  :end-date="filters.end_date"
                  :tax-region-id="filters.tax_region_id"
                  :tax-office-id="filters.tax_office_id"
                  @loading-change="objectionsLoading = $event"
                />
            </ElTabPane>
          </ElTabs>
        </ElCol>
      </ElRow>
    </DashboardCard>
  </div>
</template>

<script>
import moment from "moment";

import {Button, Col, DatePicker, Form, FormItem, Option, Row, Select, TabPane, Tabs, Switch, Radio, RadioGroup, RadioButton } from "element-ui";
// import 'element-ui/lib/theme-chalk/index.css';
import lang from 'element-ui/lib/locale/lang/en'
import locale from 'element-ui/lib/locale'
import constants from "@/constants";
import { getTaxStation } from "@/lib/utils";
import DashboardCard from "@/components/Dashboard/DashboardCard.vue";
import BarChart from "@/components/Dashboard/BarChart.vue";
import DoughnutChart from "@/components/Dashboard/DoughnutChart.vue";
import DashboardSection from "@/components/Dashboard/DashboardSection.vue";
import DashboardStat from "@/components/Dashboard/DashboardStat.vue";
import DashboardReload from "@/components/Dashboard/DashboardReload.vue";
import DashboardLoader from "@/components/Dashboard/DashboardLoader.vue";
import DashboardEmpty from "@/components/Dashboard/DashboardEmpty.vue";
import DashboardObjection from "@/components/Dashboard/DashboardObjection.vue";

locale.use(lang);

const defaultFilters = {
  tax_region_id: 0,
  tax_office_id: "",
  start_date: moment().startOf('year').format("YYYY-MM-DD"),
  end_date: moment().format("YYYY-MM-DD")
};

export default {
  components: {
    DashboardEmpty,
    DashboardLoader,
    DashboardReload,
    DashboardStat,
    DashboardSection,
    BarChart,
    DoughnutChart,
    DashboardCard,
    DashboardObjection,
    ElForm: Form,
    ElFormItem: FormItem,
    ElRow: Row,
    ElCol: Col,
    ElSelect: Select,
    ElOption: Option,
    ElDatePicker: DatePicker,
    ElTabs: Tabs,
    ElTabPane: TabPane,
    ElButton: Button,
    ElSwitch: Switch,
    ElRadio: Radio,
    ElRadioGroup: RadioGroup,
    ElRadioButton: RadioButton,
  },

  data() {
    return {
      filters: { ...defaultFilters },
      taxRegions: [{ id: 0, name: "All", status_id: 1 }],
      taxStations: {
        loading: false,
        list: []
      },
      params: "",
      entity: "taxpayers",
      entities: ["taxpayers", "returns", "assessments"],
      entitiesLabels: {
        taxpayers: 'Taxpayers',
        returns: 'Returns Filed',
        assessments: 'Assessments Raised',
      },
      taxpayers: {
        initialized: false,
        loading: false,
        params: "",
        group: "month",
        total: 0,
        rawData: { stats: null, trends: null },
        formattedData: { stats: null, trends: null },
      },
      returns: {
        initialized: false,
        loading: false,
        params: "",
        group: "month",
        total: 0,
        rawData: { stats: null, trends: null },
        formattedData: { stats: null, trends: null },
      },
      assessments: {
        initialized: false,
        loading: false,
        params: "",
        group: "month",
        total: 0,
        rawData: { stats: null, trends: null },
        formattedData: { stats: null, trends: null },
      },
      overview: {
        initialized: false,
        taxPayers: false,
        payments: false,
        assessments: false,
        directAssessments: false
      },
      taxPayersStats: {},
      paymentsStats: {},
      assessmentsStats: {},
      directAssessmentsStats: {},
      bills: {
        initialized: false,
        loading: false,
        params: "",
        amount_due: 0,
        amount_paid: 0,
        amount_outstanding: 0,
        count: 0
      },
      revenue: {
        initialized: false,
        loading: false,
        paid: 0,
        count: 0
      },
      dateLabel: "Showing data from - to -",
      hasDefaultPermissions: false,
      objectionsLoading: false,
    };
  },

  computed: {
    canAccessStats() {
      return constants.userCan(['stats.getoverview']);
    },
    canAccessReports() {
      return this.canAccessStats || constants.userCan(['report.getreport']);
    },

    computeDatepickerOptions() {
      return {
        shortcuts: [
          { text: 'This year', onClick: (picker) => {this.computePeriod('thisyear', picker)} },
          { text: 'This month', onClick: (picker) => {this.computePeriod('thismonth', picker)} },
          { text: 'This week', onClick: (picker) => {this.computePeriod('thisweek', picker)} },
          { text: 'Last week', onClick: (picker) => {this.computePeriod('lastweek', picker)} },
          { text: 'Last month', onClick: (picker) => {this.computePeriod('lastmonth', picker)} },
          { text: 'Last year', onClick: (picker) => {this.computePeriod('lastyear', picker)} }
        ]
      }
    },

    computeLoadingState() {
      return this.overview.taxPayers ||
              this.overview.payments ||
              this.overview.assessments ||
              this.overview.directAssessments ||
              this.bills.loading ||
              this.objectionsLoading ||
              (this[this.entity] && this[this.entity].loading) || false;
    }
  },

  methods: {
    handleErrors(response) {
      if (response.data) {
        for (let message in response.data) {
          this.notification(response.data[message][0], "pan_tool", "danger");
        }
      } else {
        if(response.code !== 'ACCESS_DENIED') {
          this.notification(response.message, "pan_tool", "danger");
        }
      }
    },

    notification(message, icon, color) {
      this.$notify({
        message: message,
        icon: icon,
        horizontalAlign: "right",
        verticalAlign: "top",
        type: color
      });
    },

    computePeriod(period, el) {
      let start = null;
      let end = null;

      switch (period) {
        case "thisyear":
          start = this.$moment().startOf('year');
          end = this.$moment();
          break;
        case "thismonth":
          start = this.$moment().startOf('month');
          end = this.$moment();
          break;
        case "thisweek":
          start = this.$moment().startOf('week');
          end = this.$moment();
          break;
        case "lastweek":
          start = this.$moment().subtract(1, 'week').startOf('week');
          end = this.$moment().subtract(1, 'week').endOf('week');
          break;
        case "lastmonth":
          start = this.$moment().subtract(1, 'month').startOf('month');
          end = this.$moment().subtract(1, 'month').endOf('month');
          break;
        case "lastyear":
          start = this.$moment().subtract(1, 'year').startOf('year');
          end = this.$moment().subtract(1, 'year').endOf('year');
          break;
        default:
          start = this.$moment().startOf('year');
          end = this.$moment();
          break;
      }


      if (el) {
        el.$emit('pick', start.format("YYYY-MM-DD"))
      } else {
        this.filters.start_date = start.format("YYYY-MM-DD");
      }
      this.filters.end_date = end.format("YYYY-MM-DD");
    },

    validParams() {
      if (!this.filters.start_date) {
        this.notification("You have to select a start date", "info", "info");
        return false;
      }

      if (!this.filters.end_date) {
        this.notification("You have to select an end date", "info", "info");
        return false;
      }

      if (this.filters.end_date < this.filters.start_date) {
        this.notification("The end date cannot be greater than the start date", "info", "info");
        return false;
      }

      return true
    },

    computeParams() {
      let params = "";

      const { tax_region_id, tax_office_id, start_date, end_date } = this.filters;

      if (tax_region_id) {
        params += `&tax_region_id=${tax_region_id}`
      }

      if (tax_office_id) {
        params += `&tax_office_id=${tax_office_id}`
      }

      if (start_date) {
        params += `&start_date=${start_date} 00:00:00`
      }

      if (end_date) {
        params += `&end_date=${end_date} 23:59:59`
      }

      if (start_date && end_date) {
        this.dateLabel = "Showing data from " + `<strong>${this.$moment(this.filters.start_date).format("DD MMM, YYYY")}</strong>` +
          " to " + `<strong>${this.$moment(this.filters.end_date).format("DD MMM, YYYY")}</strong>`
      }
      this.params = params;
    },

    computeStatsDataset(entity) {
      const { individuals, corporations } = this[entity].rawData.stats;
      if (!individuals || !corporations) return;

      let individualCount = 0;
      let corporationCount = 0;

      if (individuals) {
        individualCount = individuals.total_count >= 0 ? individuals.total_count : individuals;
      }
      if (corporations) {
        corporationCount = corporations.total_count >= 0 ? corporations.total_count : corporations;
      }

      return {
        labels: ['Individuals ' + this.entitiesLabels[entity], 'Corporations ' + this.entitiesLabels[entity]],
        dataset: [{ label: "Count", data: [individualCount, corporationCount] }]
      }
    },

    computeTrendsDataset(entity) {
      const { individuals, corporations } = JSON.parse(JSON.stringify(this[entity].rawData.trends));
      if (!individuals || !corporations) return;

      let data = {
        labels: [],
        dataset: [
          { label: 'Individuals ' + this.entitiesLabels[entity], backgroundColor: "#4caf50", data: [] },
          { label: 'Corporations ' + this.entitiesLabels[entity], backgroundColor: "#FF9D00", data: [] },
        ]
      }

      individuals.forEach(entry => {
        data.labels.push(entry.time_stamp)
        data.dataset[0].data.push(entry.total_count || 0)

        const inCorporation = corporations.find(c => c.time_stamp === entry.time_stamp)
        if (!inCorporation) data.dataset[1].data.push(0)
      })

      corporations.forEach(entry => {
        if (data.labels.includes(entry.time_stamp)) {
          data.dataset[1].data.push(entry.total_count || 0)
        } else {
          data.labels.push(entry.time_stamp)
          data.dataset[0].data.push(0)
          data.dataset[1].data.push(entry.total_count || 0)
        }
      })

      return data
    },

    getTaxRegions() {
      const params = "no_pages=1&order_by=name&is_asc=1"
      this.$http
        .post("tax/offices/regions", { method: "GET", params }, { params: { skipLoader: true }})
        .then(
          async (response) => {
            const { error, list, message } = response.data;
            if (response.data) {
              if (error) {
                this.handleErrors(response.data);
              } else {
                this.taxRegions = this.taxRegions.concat(list);
              }
            } else {
              this.notification(message || "An Unknown Error Occurred", "error", "danger");
            }
          },
          () => {
            this.notification("An error occurred", "error", "danger");
          }
        );
    },

    getTaxStations() {
      let params = "no_pages=1&order_by=name&is_asc=1";

      const regionId = this.filters.tax_region_id;
      if(regionId) {
        params += `&filters=[{"field": "region_id", "value": ${regionId}}]`;
      }

      this.taxStations.loading = true;
      this.$http
        .post("tax/offices", { method: "GET", params }, { params: { skipLoader: true } })
        .then(
        response => {
          const { error, list, message } = response.data;
          if (response.data) {
            if (error) {
              this.handleErrors(response.data);
            } else {
              this.filters.tax_office_id = "";
              this.taxStations.list = list;
              this.taxStations.loading = false;
            }
          } else {
            this.notification(message || "An Unknown Error Occurred", "error", "danger");
            this.taxStations.loading = false;
          }
        },
          () => {
            this.notification("An error occurred", "error", "danger");
            this.taxStations.loading = false;
          }
      );
    },

    getStats(entity) {
      if (!this.canAccessReports) return;
      if (!this.entities.includes(entity)) return;

      const urls = {
        taxpayers: "stats/tax/payers/registered/stats",
        returns: "stats/tax/returns/filed/stats",
        assessments: "stats/assessments/raised/stats",
      }
      this[entity].loading = true;

      if (!urls[entity]) return;

      this.$http.post(urls[entity],
        { method: "GET", params: this.params },
        { params: { skipLoader: true} }
      ).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this[entity].params = this.params;
              this[entity].rawData['stats'] = response.data;
              this[entity].formattedData['stats'] = this.computeStatsDataset(entity);

              const { individuals, corporations } = response.data;
              let individualCount = 0;
              let corporationCount = 0;

              if (individuals) {
                individualCount = individuals.total_count >= 0 ? individuals.total_count : individuals;
              }
              if (corporations) {
                corporationCount = corporations.total_count >= 0 ? corporations.total_count : corporations;
              }

              this[entity].total = Number(individualCount) + Number(corporationCount)

            } else {
              this.notification(
                "An error occurred when trying to load stats",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getTrends(entity) {
      if (!this.canAccessReports) return;
      if (!this.entities.includes(entity)) return;

      const urls = {
        taxpayers: "stats/tax/payers/registered/trends",
        returns: "stats/tax/returns/filed/trends",
        assessments: "stats/assessments/raised/trends",
      }
      this[entity].loading = true;

      if (!urls[entity]) return;

      this.$http.post(urls[entity],
        { method: "GET", params: this.params + `&group_by=${this[entity].group}` },
        { params: { skipLoader: true} }
      ).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this[entity].params = this.params;
              this[entity].rawData['trends'] = response.data;
              this[entity].formattedData['trends'] = this.computeTrendsDataset(entity)
            } else {
              this.notification(
                "An error occurred when trying to load stats",
                "pan_tool",
                "danger"
              );
            }
          }
          this[entity].loading = false;
        }, () => {
          this[entity].loading = false;
        }
      );
    },

    getBillsStats() {
      if (!this.canAccessReports) return;

      const billUnPaidStatus = 25;
      const billPaidStatus = 26;
      const { start_date, end_date } = this.filters;
      let filters = [
        { field: "status_id", value: [billUnPaidStatus, billPaidStatus] },
        { field: "created_at", value: this.$moment(start_date).format("YYYY-MM-DD 00:00:00"), cond_op: ">=" },
        { field: "created_at", value: this.$moment(end_date).format("YYYY-MM-DD 23:59:59"), cond_op: "<=" }
      ]
      const params = `&filters=${JSON.stringify(filters)}`;

      this.bills.loading = true;
      this.bills.initialized = true;

      this.$http.post("tax/bills/stats",
        { method: "GET", params: `${params}` },
        { params: { skipLoader: true }}).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const data = response.data;
              this.bills.count = data.total_count;
              this.bills.amount_due = data.total_amount_due;
              this.bills.amount_paid = data.total_amount_paid;
              this.bills.amount_outstanding = data.total_amount_due - data.total_amount_paid;
              this.bills.params = this.params;
            }
          }
          this.bills.loading = false;
        }, () => {
          this.bills.loading = false;
          this.notification("There was an error updating the Bills", "error", "danger");
        }
      );
    },

    getStatsAndTrend(reload = false) {
      if (!this.canAccessReports) return;
      if (!this.entities.includes(this.entity)) return;
      if (!reload && this[this.entity].params === this.params) return;

      this.taxpayers.initialized = true;
      this.getStats(this.entity);
      this.getTrends(this.entity);
    },
    handleReportsReload() {
     if (this.entity === "objections") {
        if (this.$refs.objectionsDashboard) {
          this.$refs.objectionsDashboard.reload();
        }
      } else {
        this.getStatsAndTrend(true);
      }
    },
    fetchTaxPayersStats() {
      this.overview.taxPayers = true;
      this.$http.post("stats/overview/tax/payers", { method: "GET" }, { params: { skipLoader: true }})
        .then(res => {
          this.taxPayersStats = res.data || {};
        })
        .catch(err => {
          this.notification(err.message, "error", "danger");
        })
        .finally(() => {
          this.overview.taxPayers = false;
        });
    },

    fetchPaymentsStats() {
      this.overview.payments = true;
      this.$http.post("stats/overview/payments", { method: "GET" }, { params: { skipLoader: true }})
        .then(res => {
          this.paymentsStats = res.data || {};
        })
        .catch(err => {
          this.notification(err.message, "error", "danger");
        })
        .finally(() => {
          this.overview.payments = false;
        });
    },

    fetchAssessmentsStats() {
      this.overview.assessments = true;
      this.$http.post("stats/overview/assessments", { method: "GET" }, { params: { skipLoader: true }})
        .then(res => {
          this.assessmentsStats = res.data || {};
        })
        .catch(err => {
          this.notification(err.message, "error", "danger");
        })
        .finally(() => {
          this.overview.assessments = false;
        });
    },

    fetchDirectAssessmentsStats() {
      this.overview.directAssessments = true;
      this.$http.post("stats/overview/direct/assessments", { method: "GET" }, { params: { skipLoader: true }})
        .then(res => {
          this.directAssessmentsStats = res.data || {};
        })
        .catch(err => {
          this.notification(err.message, "error", "danger");
        })
        .finally(() => {
          this.overview.directAssessments = false;
        });
    },

    getStatsOverview() {
      if (!this.canAccessStats) return;

      this.overview.initialized = true;

      // fire off all 4 in parallel
      this.fetchTaxPayersStats();
      this.fetchPaymentsStats();
      this.fetchAssessmentsStats();
      this.fetchDirectAssessmentsStats();
    },

    resetStats() {
      this.period = "thisyear";
      this.filters = { ...defaultFilters };
      this.filterStats();
    },

    filterStats() {
      if (!this.validParams()) return;

      this.computeParams();
      this.getBillsStats();
      this.getStatsAndTrend(true);
      this.$nextTick(() => {
        if (this.$refs.objectionsDashboard) {
          this.$refs.objectionsDashboard.reload();
        }
      });
    },

    formatValue(value) {
      if (isNaN(value)) return value;
      if (value >= 1e12) {
        return (value / 1e12).toFixed(3) + 'T';
      } else if (value >= 1e9) {
        return (value / 1e9).toFixed(3) + 'B';
      } else if (value >= 1e6) {
        return (value / 1e6).toFixed(3) + 'M';
      } else {
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2})
      }
    },

    formatCount(value) {
      return Number(value).toLocaleString()
    },

    formatAmount(value) {
      return '₦' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 2})
    },

    computeValue(value, type = 'amount') {
      if (!value) {
        return {
          value: type === 'amount' ? '₦' + 0.00 : 0,
          formatted_value: type === 'amount' ? '₦' + 0.00 : 0
        }
      }
      return {
        value: (type === 'amount' ? '₦' : '') + Number(value).toLocaleString(undefined, { minimumFractionDigits: 2}),
        formatted_value: (type === 'amount' ? '₦' : '') +  this.formatValue(value)
      }
    }
  },

  created() {
    const office = getTaxStation();
    if (office && office.id && office.region) {
      this.filters.tax_region_id = office.region.id;
      this.filters.tax_office_id = office.id;
    }
    this.getTaxRegions();
    this.getTaxStations();
    this.computeParams();
  }
};
</script>

<style lang="scss" scoped>
@import "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Source+Serif+Pro:wght@600&display=swap";

.dashboard {
  font-family: "Figtree", sans-serif !important;
  padding: 0 15px;

  h1, h2, h3, h4, h5, h6 {
    font-family: "Figtree", sans-serif;
  }
}

.el-select {
  min-width: 100px;
  width: 100%;
}

.el-select-dropdown__item.selected {
  color: #00c853 !important;
}
.el-select :deep(.el-input .el-input__inner:focus),
.el-select :deep(.el-input.is-focus .el-input__inner),
.el-date-editor :deep(.el-input__inner:focus) {
  border-color: #00c853 !important;
}

.el-date-editor.el-input,
.el-date-editor.el-input__inner {
  width: 100% !important;
}

:deep(.el-form-item__label) {
  line-height: 20px !important;
}

.el-button--danger {
  background-color: #f44336;
  border-color: #f44336;
}

.el-button--danger.is-disabled {
  background-color: #f44336;
  border-color: #f44336;
  opacity: 0.5;
}

.el-button--primary,
.el-button--primary:focus,
.el-button--primary:active,
.el-button--primary:hover {
  background-color: #4caf50;
  border-color: #4caf50;
}

.el-button--primary.is-disabled,
.el-button--primary.is-disabled:active,
.el-button--primary.is-disabled:focus,
.el-button--primary.is-disabled:hover {
  background-color: #4caf50;
  border-color: #4caf50;
  opacity: 0.5;
}

:deep(.el-radio-button__orig-radio:checked+.el-radio-button__inner) {
  background-color: #4caf50;
  border-color: #4caf50;
}

:deep(.el-radio-button__orig-radio:checked+.el-radio-button__inner:hover) {
  color: #fff !important;
}

:deep(.el-tabs__item.is-active),
:deep(.el-tabs__item:hover),
:deep(.el-radio-button__inner:hover) {
  color: #4caf50;
}

:deep(.el-tabs__active-bar) {
  background-color: #4caf50;
}

.dashboard-group {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;

  p {
    font-weight: 600;
  }
}

.dashboard-graph {
  position: relative;
  min-height: 400px;
}
</style>
